import React, { useMemo, useRef, useEffect } from 'react';

import { Flexbox, FontAwesomeIcon } from '@schoolinks/ui-components';

import cn from 'classnames';
import { isFunction } from 'lodash';
import { string, element, bool } from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';

import { faVolumeHigh } from '@fortawesome/pro-regular-svg-icons/faVolumeHigh';
import { faWaveform } from '@fortawesome/pro-regular-svg-icons/faWaveform';

import Tooltip from '@mui/material/Tooltip';

import ClickableComponent from 'components/Common/ClickableComponent';

import { getSelectedLanguage } from 'i18n/utils';
import { ensureArray } from 'utils/arrays';

import { ensureObject } from 'utils/objects';

import {
  PLAYING,
  DEFAULT,
  TTS_PLAY_ONE,
  TTS_PLAY_ALL,
} from 'modules/ElementaryCareers/constants';

import useStopPlayingReadAloud from 'modules/ElementaryCareers/hooks/useStopPlayingReadAloud';
import {
  setTtsEnabledStatus,
  getAudio,
} from 'modules/ElementaryCareers/selectors';
import { SET_TTS_DATA } from 'modules/ElementaryCareers/store/actionTypes';

import s from './Speaker.css';

const Speaker = ({ children, iconClass, rootClass, dataTtsId, smallIcon }) => {
  const uniqueIdRef = useRef(null);
  const dispatch = useDispatch();
  const { ttsOption, playingItem, ttsEnabledItems, audioFiles } = useSelector(
    state => state?.textToSpeech,
  );
  const language = getSelectedLanguage();

  const stopPlayingReadAloud = useStopPlayingReadAloud();

  useEffect(() => {
    // Generate a unique ID when the component mounts
    uniqueIdRef.current = Math.ceil(Math.random() * 10000);
  }, []);

  const uniqueDataTtsId =
    typeof dataTtsId === 'string'
      ? `${dataTtsId.replace(/['"]/g, '').trim()}-${uniqueIdRef.current}-${language}`
      : `random-${uniqueIdRef.current}-${language}`;

  const itemIndex = useMemo(
    () =>
      ensureArray(ttsEnabledItems).findIndex(
        ({ id }) => id === uniqueDataTtsId,
      ),
    [ttsEnabledItems, uniqueDataTtsId],
  );

  const isPlaying = itemIndex === playingItem;

  const tooltipTitle = isPlaying ? 'Stop Reading' : 'Start Reading';

  const handleIconClick = event => {
    event.preventDefault();
    event.stopPropagation();
    event.nativeEvent.stopImmediatePropagation();

    stopPlayingReadAloud();

    const { id, url } = ensureObject(ttsEnabledItems?.[itemIndex]);

    const audio = getAudio({ id, url, audioFiles, dispatch });

    if (!audio) {
      return;
    }

    if (!audio.paused && isFunction(audio.pause)) {
      audio.currentTime = 0;
      audio.pause();
      setTtsEnabledStatus(uniqueDataTtsId, DEFAULT);
    } else if (audio.paused) {
      setTtsEnabledStatus(uniqueDataTtsId, PLAYING);
      audio.play().then(() => {
        audio.addEventListener('ended', () => {
          setTtsEnabledStatus(uniqueDataTtsId, DEFAULT);
          dispatch({
            type: SET_TTS_DATA,
            payload: {
              ttsOption: TTS_PLAY_ALL,
              isPlaying: false,
            },
          });
        });
      });
    }

    if (itemIndex !== -1) {
      dispatch({
        type: SET_TTS_DATA,
        payload: {
          ttsOption: TTS_PLAY_ONE,
          playingItem: itemIndex,
          isPlaying: true,
        },
      });
    }
  };

  return (
    <Flexbox
      alignItems="center"
      className={cn(s.root, rootClass, { [s.smallIcon]: smallIcon })}
      data-tts-enabled={DEFAULT}
      data-tts-id={uniqueDataTtsId}
      gap={0.5}
    >
      {children}
      {ttsOption ? (
        <Tooltip arrow placement="bottom" title={tooltipTitle}>
          <ClickableComponent
            className={cn(s.iconWrapper, iconClass)}
            onClick={handleIconClick}
          >
            <FontAwesomeIcon className={s.waveIcon} icon={faWaveform} />
            <FontAwesomeIcon className={s.speakerIcon} icon={faVolumeHigh} />
          </ClickableComponent>
        </Tooltip>
      ) : null}
    </Flexbox>
  );
};

Speaker.propTypes = {
  rootClass: string,
  iconClass: string,
  children: element.isRequired,
  dataTtsId: string.isRequired,
  smallIcon: bool,
};

Speaker.defaultProps = {
  iconClass: '',
  rootClass: '',
  smallIcon: false,
};

export default Speaker;
